/**
 * Canonical vocabulary for region and data residency (PE-1579, decision D-12).
 *
 * This module is the single source of truth for region and residency. Every
 * region-keyed mapping (residency -> region, region -> per-service AWS regions,
 * region -> deployment infrastructure, and the pre-login signal maps) lives
 * here and nowhere else. No other module may declare a table keyed by a
 * residency or region literal; consume the helpers below instead. A guard test
 * (`__tests__/regionAndResidency.guard.test.ts`) enforces this.
 *
 * Glossary
 * --------
 * - AWS region: the genuine AWS notion. A plain region string such as
 *   `us-east-1`, `us-west-2`, or `eu-central-1`. The word "region" on its own
 *   always means this.
 *
 * - backend region: the PRIMARY AWS region of the backend that serves a user
 *   cohort - the region where that cohort's Lambdas, API Gateway, Cognito user
 *   pools, and S3 buckets reside. Its value IS an AWS region string. The
 *   secondary data-tier region and every other per-service region are derived
 *   from `BACKEND_REGION_SERVICES`. A backend region is named for where the
 *   stack physically runs, never for the cohort it serves. It is NOT the user's
 *   physical location and NOT a residency zone.
 *
 * - data residency: the authoritative account attribute (Cognito
 *   `custom:data_residency`). The policy zone that decides whether and which PII
 *   vault applies. A zone can span multiple countries (e.g. `ARE` covers UAE and
 *   Qatar home countries today). Stored and authoritative for routing.
 *
 * - home country: the country the user is from (Cognito `custom:home_country`,
 *   ISO 3166-1 alpha-3). Finer than residency and one input to resolving it, not
 *   the stored source of truth for residency.
 *
 * Derivation chain:
 *   signals -> home_country -> data_residency -> backend region -> per-service AWS regions
 */

/**
 * Data-residency zone. Authoritative account attribute; selects the PII vault.
 * ISO 3166-1 alpha-3, uppercase (see decision D-11).
 */
export type DataResidency = 'USA' | 'ARE';

/**
 * Home country: the user's country of origin (Cognito `custom:home_country`,
 * ISO 3166-1 alpha-3, uppercase). Any ISO alpha-3 code is valid - a user can
 * come from anywhere - so this is a string. The known values below are listed
 * for documentation and editor autocomplete only; `UNK` is the unknown-origin
 * sentinel. Home country is finer than residency (many countries share a zone)
 * and derives to a residency via `residencyForHomeCountry`.
 */
export type HomeCountry = 'USA' | 'ARE' | 'QAT' | 'UNK' | (string & {});

/**
 * Backend region: the primary AWS region of the backend serving a user cohort
 * (Lambda, API Gateway, Cognito user pools, S3). The value is an AWS region
 * string. The set is intentionally small - it is the regions we actually run a
 * primary stack in, not every AWS region.
 */
export type BackendRegion = 'us-east-1' | 'eu-central-1';

export const RESIDENCY_TO_BACKEND_REGION: Record<DataResidency, BackendRegion> =
  {
    USA: 'us-east-1',
    ARE: 'eu-central-1',
  };

/**
 * Home country -> residency zone, for the countries that resolve to a
 * NON-default zone. This is a partial map keyed by ISO 3166-1 alpha-3. Every
 * other home country - including the `UNK` sentinel and any country not listed
 * here - derives to `DEFAULT_DATA_RESIDENCY` (`USA`) via
 * `residencyForHomeCountry`.
 *
 * ONLY `ARE` maps to the `ARE` zone for now. Qatari (`QAT`) accounts are
 * deliberately managed in the US backend until a future migration project
 * moves them to the UAE zone; when that lands, `QAT: 'ARE'` returns here (and
 * `QA` in `CLOUDFRONT_COUNTRY_TO_RESIDENCY` below) as the single switch.
 */
export const HOME_COUNTRY_TO_RESIDENCY: Readonly<
  Record<string, DataResidency>
> = {
  ARE: 'ARE',
};

/**
 * Residency zone for any home country not present in
 * `HOME_COUNTRY_TO_RESIDENCY` (including unknown origin).
 */
export const DEFAULT_DATA_RESIDENCY: DataResidency = 'USA';

/**
 * Per-service AWS regions for a given backend region. This table is where all
 * region asymmetry lives:
 * - `us-east-1` compute pairs with `us-west-2` DynamoDB.
 * - SES is pinned to `us-east-1` for every backend region (SES is not deployed
 *   elsewhere).
 * - Redshift is pinned to `us-west-2` for every backend region (single
 *   warehouse).
 */
export interface RegionServiceConfig {
  /** Lambda / API Gateway. */
  compute: string;
  /** Cognito user pools. */
  cognito: string;
  dynamodb: string;
  s3: string;
  ses: string;
  redshift: string;
}

export const BACKEND_REGION_SERVICES: Record<
  BackendRegion,
  RegionServiceConfig
> = {
  'us-east-1': {
    compute: 'us-east-1',
    cognito: 'us-east-1',
    dynamodb: 'us-west-2',
    s3: 'us-east-1',
    ses: 'us-east-1',
    redshift: 'us-west-2',
  },
  'eu-central-1': {
    compute: 'eu-central-1',
    cognito: 'eu-central-1',
    dynamodb: 'eu-central-1',
    s3: 'eu-central-1',
    ses: 'us-east-1',
    redshift: 'us-west-2',
  },
};

/**
 * Deployment infrastructure for a backend region: the concrete resource names
 * (S3 buckets, API Gateway origin) and the human-facing label. This is the
 * other half of the region-keyed data - `BACKEND_REGION_SERVICES` says which
 * AWS region each service runs in, this says which named resources a request is
 * routed to. Both live here so a backend region is described in exactly one
 * place.
 */
export interface RegionInfraConfig {
  /** AWS Service (access) logs bucket. */
  logsBucket: string;
  /** User-generated-content bucket. */
  ucgBucket: string;
  /** Lesson-database / content bucket. */
  ldbBucket: string;
  /** API Gateway origin for the stack. */
  apiGatewayUrl: string;
  /** Human-facing label for logs and CLI output. */
  displayName: string;
}

export const BACKEND_REGION_INFRA: Record<BackendRegion, RegionInfraConfig> = {
  'us-east-1': {
    logsBucket: 'peekapak-aws-logs',
    ucgBucket: 'ucg.peekapak.com',
    ldbBucket: 'ldb.peekapak.com',
    apiGatewayUrl: 'https://api.peekapak.com',
    displayName: 'United States',
  },
  'eu-central-1': {
    logsBucket: 'peekapak-aws-logs-fra',
    ucgBucket: 'ucg-fra.peekapak.com',
    ldbBucket: 'ldb-fra.peekapak.com',
    apiGatewayUrl: 'https://api-fra.peekapak.com',
    displayName: 'Frankfurt (EU Central)',
  },
};

/**
 * Residency-resolution signal maps. These translate pre-login signals into a
 * residency zone. They live here (not in the resolver) so every input->zone
 * mapping is region-keyed data in the single source of truth.
 */

/** Subdomain label -> residency zone (e.g. `api-fra`/`fra` -> ARE). */
export const SUBDOMAIN_TO_RESIDENCY: Record<string, DataResidency> = {
  'api-fra': 'ARE',
  fra: 'ARE',
  www: 'USA',
};

/**
 * CloudFront-Viewer-Country (ISO 3166-1 alpha-2) -> residency zone. Qatar (QA)
 * deliberately resolves to the default `USA` zone for now (accounts managed in
 * the US pending the future QAT->UAE migration project); see
 * `HOME_COUNTRY_TO_RESIDENCY`.
 */
export const CLOUDFRONT_COUNTRY_TO_RESIDENCY: Record<string, DataResidency> = {
  AE: 'ARE',
  US: 'USA',
};

/** The complete set of backend regions we run a primary stack in. */
export const BACKEND_REGIONS: readonly BackendRegion[] = [
  'us-east-1',
  'eu-central-1',
];

/** The complete set of residency zones. */
export const DATA_RESIDENCIES: readonly DataResidency[] = ['USA', 'ARE'];

/** Resolve the primary backend region for a residency zone. */
export function backendRegionForResidency(
  residency: DataResidency,
): BackendRegion {
  return RESIDENCY_TO_BACKEND_REGION[residency];
}

/**
 * Resolve the residency zone served by a backend region (the inverse of
 * `backendRegionForResidency`). Each backend region serves exactly one zone.
 */
export function residencyForBackendRegion(
  backendRegion: BackendRegion,
): DataResidency {
  return backendRegion === 'eu-central-1' ? 'ARE' : 'USA';
}

/**
 * Resolve the residency zone implied by a home country. Only `ARE` maps to the
 * `ARE` zone (QAT is US-managed pending its migration project - see
 * `HOME_COUNTRY_TO_RESIDENCY`); every other country, the `UNK` sentinel, and
 * empty/unknown input fall back to `DEFAULT_DATA_RESIDENCY`.
 */
export function residencyForHomeCountry(
  homeCountry: HomeCountry,
): DataResidency {
  const normalized = homeCountry?.trim().toUpperCase() ?? '';
  return HOME_COUNTRY_TO_RESIDENCY[normalized] ?? DEFAULT_DATA_RESIDENCY;
}

/** Look up every per-service AWS region for a backend region. */
export function servicesForBackendRegion(
  backendRegion: BackendRegion,
): RegionServiceConfig {
  return BACKEND_REGION_SERVICES[backendRegion];
}

/** Look up the deployment infrastructure (buckets, origin, label) for a region. */
export function infraForBackendRegion(
  backendRegion: BackendRegion,
): RegionInfraConfig {
  return BACKEND_REGION_INFRA[backendRegion];
}

/**
 * Narrow an arbitrary string to a `BackendRegion`, or `undefined` if it is not
 * one of the regions we run a stack in.
 */
export function asBackendRegion(value?: string): BackendRegion | undefined {
  return (BACKEND_REGIONS as readonly string[]).includes(value ?? '')
    ? (value as BackendRegion)
    : undefined;
}

/**
 * Narrow an arbitrary string to a `DataResidency` (case-insensitive), or
 * `undefined` if it is not a known residency zone.
 */
export function asDataResidency(value?: string): DataResidency | undefined {
  const normalized = value?.trim().toUpperCase();
  return normalized === 'USA' || normalized === 'ARE'
    ? (normalized as DataResidency)
    : undefined;
}
